import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Quote } from 'lucide-react';
import { Banner } from '@/components/ui/Banner';
import { Button, ButtonAnchor } from '@/components/ui/Button';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Skeleton } from '@/components/ui/Skeleton';
import { SlideOver } from '@/components/ui/SlideOver';
import { FormsScoreDisplay } from '@/components/patient/FormsScoreDisplay';
import {
  useFormsAssignmentDetail,
  type FormsQuestion,
} from '@/hooks/useFormsAssignmentResponses';
import { answerPoints, formatResponse } from '@/lib/formsAnswerValue';
import { resolveAssignmentScores } from '@/lib/formsScoring';
import { formsResponseUrl } from '@/lib/formsUrls';
import { journalQuoteHtml } from '@/lib/journalQuote';
import { patientJournalWritePath } from '@/lib/journalWritePath';
import {
  questionnaireSeverityBands,
  scoreNumericValue,
  shownQuestionnaireScore,
} from '@/lib/questionnaireScoreDisplay';
import { formatClinicDateOnly } from '@/lib/questionnaireDateFormat';
import { getSeverityLabel, type Severity } from '@/lib/severityUtils';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

function looksDanish(text: string): boolean {
  return /[æøåÆØÅ]/.test(text) || /\b(og|eller|ikke|med|til|fra|den|det|en|et|på|af)\b/i.test(text);
}

function looksEnglishBoilerplate(text: string): boolean {
  return /\b(rand|health survey|quality of life|please|questionnaire)\b/i.test(text) && !looksDanish(text);
}

function introCopy(questionnaire: {
  description?: string | null;
  description_da?: string | null;
  description_en?: string | null;
} | null | undefined): { text: string; englishOnly: boolean } | null {
  if (!questionnaire) return null;
  const da = questionnaire.description_da?.trim();
  if (da) return { text: da, englishOnly: false };
  const raw = (questionnaire.description ?? questionnaire.description_en ?? '').trim();
  if (!raw) return null;
  if (looksDanish(raw)) return { text: raw, englishOnly: false };
  if (looksEnglishBoilerplate(raw)) return { text: raw, englishOnly: true };
  return { text: raw, englishOnly: !looksDanish(raw) };
}

export type ResponseHistoryPoint = {
  formsAssignmentId: string;
  completedAt: string | null;
  score: Record<string, unknown> | null;
};

function questionText(question: FormsQuestion): string {
  const da = (question as { text_da?: string }).text_da;
  return (typeof da === 'string' && da.trim()) || question.text;
}

function bandMarkerPercent(value: number, bands: { min: number; max: number }[]): number {
  const last = bands[bands.length - 1];
  const max = last?.max ?? 1;
  return Math.min(100, Math.max(0, (value / max) * 100));
}

function previousResponsePoint(
  history: ResponseHistoryPoint[],
  currentId: string,
  currentCompletedAt?: string | null,
): ResponseHistoryPoint | null {
  const currentMs = currentCompletedAt
    ? Date.parse(currentCompletedAt)
    : Number.POSITIVE_INFINITY;
  let best: ResponseHistoryPoint | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const row of history) {
    if (row.formsAssignmentId === currentId || !row.completedAt) continue;
    const ms = Date.parse(row.completedAt);
    if (!Number.isFinite(ms) || ms > currentMs) continue;
    if (ms >= bestMs) {
      best = row;
      bestMs = ms;
    }
  }
  return best;
}

export function FormsResponsePanel({
  clinicId,
  patientId,
  formsAssignmentId,
  templateName,
  completedAt,
  appointmentLabel,
  history = [],
  onClose,
}: {
  clinicId: string;
  patientId?: string;
  formsAssignmentId: string;
  templateName?: string;
  completedAt?: string | null;
  appointmentLabel?: string | null;
  history?: ResponseHistoryPoint[];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, isError, error } = useFormsAssignmentDetail(clinicId, formsAssignmentId);
  const [onlyDeviant, setOnlyDeviant] = useState(false);
  const name = templateName || data?.questionnaire?.name || t('forms.responses.title', 'Svar');
  const adminUrl = data?.admin_url ?? formsResponseUrl(formsAssignmentId);
  const questions = useMemo(
    () => data?.questionnaire?.questions ?? [],
    [data?.questionnaire?.questions],
  );
  const shownScore = useMemo(
    () =>
      shownQuestionnaireScore({
        storedScore: data?.score_details,
        templateName: name,
        questions,
        responses: data?.responses,
        scoringConfig: data?.questionnaire?.scoring_config ?? null,
      }),
    [data?.questionnaire?.scoring_config, data?.responses, data?.score_details, name, questions],
  );
  const scoreResult = useMemo(() => {
    if (!data) return null;
    return resolveAssignmentScores(
      data.questionnaire?.questions ?? [],
      data.responses,
      data.questionnaire?.scoring_config,
      data.score_details,
    );
  }, [data]);
  const previousPoint = useMemo(
    () => previousResponsePoint(history, formsAssignmentId, completedAt ?? data?.completed_at),
    [completedAt, data?.completed_at, formsAssignmentId, history],
  );
  const previousFromStored = useMemo(() => {
    if (!previousPoint?.score) return null;
    return resolveAssignmentScores([], {}, null, previousPoint.score);
  }, [previousPoint]);
  const previousDetail = useFormsAssignmentDetail(
    clinicId,
    previousPoint?.formsAssignmentId,
    Boolean(scoreResult && previousPoint && !previousFromStored?.domainScores.length),
  );
  const previousDomains = useMemo(() => {
    if (previousFromStored?.domainScores.length) return previousFromStored.domainScores;
    if (!previousDetail.data) return undefined;
    const resolved = resolveAssignmentScores(
      previousDetail.data.questionnaire?.questions ?? [],
      previousDetail.data.responses,
      previousDetail.data.questionnaire?.scoring_config,
      previousDetail.data.score_details,
    );
    return resolved?.domainScores.length ? resolved.domainScores : undefined;
  }, [previousDetail.data, previousFromStored]);
  const bands = questionnaireSeverityBands(name);
  const numeric = scoreNumericValue(shownScore);
  const severity = shownScore.kind === 'total' ? shownScore.severity : null;
  const intro = introCopy(data?.questionnaire);

  const answers = useMemo(() => {
    return questions.map((question, index) => {
      const points = answerPoints(question, data?.responses[question.id]);
      const deviant = points != null && points >= 2 && Boolean(bands);
      return {
        question,
        index,
        answer: formatResponse(question, data?.responses[question.id], i18n.language),
        points,
        deviant,
      };
    });
  }, [bands, data?.responses, i18n.language, questions]);

  const deviantCount = answers.filter((row) => row.deviant).length;
  const visible = onlyDeviant ? answers.filter((row) => row.deviant) : answers;
  const historyPoints = history
    .filter((row) => row.completedAt)
    .map((row) => ({
      ...row,
      value: scoreNumericValue(
        shownQuestionnaireScore({ storedScore: row.score, templateName: name }),
      ),
    }))
    .filter((row) => row.value != null)
    .slice()
    .reverse();

  function cite() {
    if (!patientId) return;
    const text = answers
      .map((row) => `${row.index + 1}. ${questionText(row.question)}\n${row.answer}`)
      .join('\n\n');
    const html = journalQuoteHtml({
      text,
      attribution: t('forms.responses.cite_attribution', {
        name,
        date: formatClinicDateOnly(completedAt ?? data?.completed_at, i18n.language),
      }),
    });
    navigate(
      patientJournalWritePath(patientId, {
        source: 'journal',
        from: `${location.pathname}${location.search}`,
      }),
      { state: { citeHtml: html } },
    );
  }

  return (
    <SlideOver
      open
      width={560}
      title={name}
      description={
        <>
          <span className="num">
            {t('forms.responses.completed_on', {
              date: formatClinicDateOnly(completedAt ?? data?.completed_at, i18n.language),
              defaultValue: `Udfyldt ${formatClinicDateOnly(completedAt ?? data?.completed_at, i18n.language)}`,
            })}
          </span>
          {appointmentLabel ? <span className="mt-0.5 block num">{appointmentLabel}</span> : null}
        </>
      }
      closeLabel={t('forms.responses.close', 'Luk')}
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex flex-col gap-s3">
          <Skeleton className="h-16 w-40" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : null}
      {isError ? (
        <Banner tone="error" title={t('forms.responses.load_error', 'Kunne ikke hente svar')}>
          <p className={TYPE.small}>{error?.message}</p>
          <ButtonAnchor href={adminUrl} target="_blank" rel="noopener noreferrer" variant="link" className="mt-s2">
            <ExternalLink />
            {t('forms.responses.open_in_forms_fallback', 'Åbn i Clinio Forms i stedet')}
          </ButtonAnchor>
        </Banner>
      ) : null}
      {data && !isLoading ? (
        <div className="sv sk">
          {shownScore.kind === 'total' ? (
            <div className="sv-top">
              <div className="sv-score num">
                {shownScore.value}
                {shownScore.max != null ? <small>/{shownScore.max}</small> : null}
              </div>
              <div>
                {severity ? (
                  <>
                    <p className="sv-word">{getSeverityLabel(severity, t)}</p>
                    <p>
                      {t('forms.responses.overall', {
                        severity: severityPhrase(severity, t),
                        defaultValue: `Samlet score · ${severityPhrase(severity, t)}`,
                      })}
                    </p>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}
          {shownScore.kind === 'physical_mental' ? (
            <div className="sv-top">
              <div className="sv-score num">{shownScore.physical}</div>
              <div>
                <p className="sv-word">
                  {t('forms.score_physical', { value: shownScore.physical })}
                  {t('forms.score_mental', { value: shownScore.mental })}
                </p>
              </div>
            </div>
          ) : null}
          {bands && numeric != null ? (
            <div className="sv-band" aria-label={t('forms.responses.scale', 'Scoreskala')}>
              <div className="sv-bar" style={{ gridTemplateColumns: bands.map((band) => `${band.max - band.min + 1}fr`).join(' ') }}>
                {bands.map((band) => (
                  <i key={band.severity} className={severity === band.severity ? 'on' : ''} />
                ))}
                <span className="mk" style={{ left: `calc(${bandMarkerPercent(numeric, bands)}% - 2px)` }} aria-hidden />
              </div>
              <div className="sv-lb num" style={{ gridTemplateColumns: bands.map((band) => `${band.max - band.min + 1}fr`).join(' ') }}>
                {bands.map((band) => (
                  <span key={band.severity} className={severity === band.severity ? 'on' : ''}>
                    <b>{getSeverityLabel(band.severity, t)}</b>
                    {band.min}–{band.max}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {scoreResult?.domainScores.length ? (
            <FormsScoreDisplay
              domainScores={scoreResult.domainScores}
              previousDomainScores={previousDomains}
              totalScore={scoreResult.totalScore}
            />
          ) : null}
          {historyPoints.length > 1 ? (
            <div className="sv-box">
              <p className="h4">
                {t('forms.responses.history', {
                  count: historyPoints.length,
                  defaultValue: `Udvikling · ${historyPoints.length} besvarelser`,
                })}
              </p>
              <HistoryChart points={historyPoints} language={i18n.language} name={name} />
            </div>
          ) : null}
          <div className="sv-acts">
            <ButtonAnchor href={adminUrl} target="_blank" rel="noopener noreferrer" variant="secondary">
              <ExternalLink />
              {t('forms.responses.open_in_forms', 'Åbn i Clinio Forms')}
            </ButtonAnchor>
            {patientId ? (
              <Button type="button" variant="secondary" onClick={cite}>
                <Quote />
                {t('forms.responses.cite', 'Citér i journal')}
              </Button>
            ) : null}
          </div>
          <div className="sv-sep" />
          <p className="h4">{t('forms.responses.answers', 'Svar')}</p>
          {intro ? (
            <>
              {intro.englishOnly ? (
                <p className="sv-en">{t('forms.responses.english_only', 'Kun på engelsk')}</p>
              ) : null}
              <p className="sv-q">{intro.text}</p>
            </>
          ) : null}
          {bands ? (
            <div className="sv-tog">
              <SegmentedControl
                ariaLabel={t('forms.responses.answers', 'Svar')}
                value={onlyDeviant ? 'deviant' : 'all'}
                onChange={(value) => setOnlyDeviant(value === 'deviant')}
                options={[
                  { value: 'deviant', label: t('forms.responses.show_deviant', 'Vis kun afvigende svar') },
                  {
                    value: 'all',
                    label: t('forms.responses.show_all', {
                      count: answers.length,
                      defaultValue: `Vis alle ${answers.length} svar`,
                    }),
                  },
                ]}
              />
              <span className="h">
                {t('forms.responses.deviant_hint', {
                  count: deviantCount,
                  total: answers.length,
                  defaultValue: `Afvigende: 2 eller 3 point (${deviantCount} af ${answers.length})`,
                })}
              </span>
            </div>
          ) : null}
          <ol className="ans">
            {visible.map((row) => (
              <li key={row.question.id} className={cn(row.deviant && 'dev')}>
                <span className="n num">{row.index + 1}</span>
                <div>
                  <p className="q">{questionText(row.question)}</p>
                  <p className="a">
                    {row.answer}
                    {row.deviant ? <span className="devt">{t('forms.responses.deviant', 'Afvigende')}</span> : null}
                  </p>
                </div>
                <span className="p num">
                  {row.points != null
                    ? t('forms.responses.points', { count: row.points, defaultValue: `${row.points} point` })
                    : ''}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </SlideOver>
  );
}

function severityPhrase(
  severity: Severity,
  t: (key: string, fallback: string) => string,
): string {
  switch (severity) {
    case 'minimal':
      return t('forms.responses.severity_minimal_phrase', 'minimal grad af symptomer');
    case 'mild':
      return t('forms.responses.severity_mild_phrase', 'let grad af depressive symptomer');
    case 'moderate':
      return t('forms.responses.severity_moderate_phrase', 'moderat grad af symptomer');
    case 'moderately_severe':
      return t('forms.responses.severity_modsev_phrase', 'moderat-svær grad af symptomer');
    case 'severe':
      return t('forms.responses.severity_severe_phrase', 'svær grad af symptomer');
    default: {
      const _exhaustive: never = severity;
      return _exhaustive;
    }
  }
}

function HistoryChart({
  points,
  language,
  name,
}: {
  points: { value: number | null; completedAt: string | null }[];
  language: string;
  name: string;
}) {
  const width = 476;
  const height = 150;
  const left = 36;
  const right = 8;
  const top = 24;
  const bottom = 30;
  const values = points.map((point) => point.value ?? 0);
  const max = Math.max(27, ...values);
  const y = (value: number) => top + ((max - value) / max) * (height - top - bottom);
  const xs = points.map((_, index) =>
    points.length === 1
      ? (width + left - right) / 2
      : left + 44 + (index * (width - left - right - 88)) / Math.max(1, points.length - 1),
  );
  const line = xs.map((x, index) => `${x.toFixed(1)},${y(values[index] ?? 0).toFixed(1)}`).join(' ');
  const ticks = [0, Math.round(max / 2), max];
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`${name}: ${points.map((point) => `${point.value} ${formatClinicDateOnly(point.completedAt, language)}`).join(', ')}`}
      className="mt-2 block max-w-full"
    >
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={left}
            x2={width - right}
            y1={y(tick)}
            y2={y(tick)}
            stroke="var(--line)"
            strokeDasharray={tick === 0 ? '0' : '3 4'}
          />
          <text x={left - 8} y={y(tick) + 4} textAnchor="end" fontSize="11" fill="var(--muted)">
            {tick}
          </text>
        </g>
      ))}
      <polyline points={line} fill="none" stroke="var(--teal)" strokeWidth="2" strokeLinejoin="round" />
      {xs.map((x, index) => {
        const last = index === xs.length - 1;
        return (
          <g key={points[index]?.completedAt ?? index}>
            <circle
              cx={x}
              cy={y(values[index] ?? 0)}
              r={last ? 5 : 4}
              fill={last ? 'var(--teal)' : 'var(--card)'}
              stroke="var(--teal)"
              strokeWidth="2"
            />
            <text x={x} y={y(values[index] ?? 0) - 11} textAnchor="middle" fontSize="13" fontWeight="600" fill="var(--text)">
              {values[index]}
            </text>
            <text x={x} y={height - 8} textAnchor="middle" fontSize="12" fill="var(--muted)">
              {formatClinicDateOnly(points[index]?.completedAt, language)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function FormsResponseViewModal({
  clinicId,
  formsAssignmentId,
  templateName,
  patientId,
  completedAt,
  appointmentLabel,
  history,
  onClose,
}: {
  clinicId: string;
  formsAssignmentId: string;
  templateName?: string;
  patientId?: string;
  completedAt?: string | null;
  appointmentLabel?: string | null;
  history?: ResponseHistoryPoint[];
  onClose: () => void;
}) {
  return (
    <FormsResponsePanel
      clinicId={clinicId}
      patientId={patientId}
      formsAssignmentId={formsAssignmentId}
      templateName={templateName}
      completedAt={completedAt}
      appointmentLabel={appointmentLabel}
      history={history}
      onClose={onClose}
    />
  );
}
